<template>
    <div class="lp-location-finder" :class="cssClass">
        <div class="lp-location-finder__list">
            <div class="container">
                <div class="input-group input-group--short">
                    <button class="btn btn-secondary"
                            type="button"
                            aria-label="Get location"
                            @click="getLocation">
                        <i class="far fa-location"></i>
                    </button>
                    <input type="text"
                           class="form-control"
                           :placeholder="searchPlaceholder"
                           v-model="SearchManager.SearchInput"
                           @input="SearchManager.SetSearch"
                           aria-label="Search locations" 
                    />
                </div>

                <p class="d-none lp-location-finder__filters-title">Filters</p>

                <tag-filter :options="LocationTypeManager.ExternalSource"
                            class="d-none lp-shared-source"
                            @option-clicked="sharedSourceClickHandler" />

                <tag-filter :options="LocationTypeManager.InternalSource"
                            class="d-none mb-4"
                            @option-clicked="ourBranchClickHandler" />

                <tag-filter :options="CustomFilterManager.Filters"
                            class="d-none mb-4"
                            @option-clicked="filterClickHadler" />
                <location-list :visible-locations="visibleLocations" :details-url="detailsUrl" :enable-details="enableDetails"/>
            </div>
        </div>
        <div class="lp-location-finder__map" aria-hidden="true">
            <google-map :apiKey="apiKey"
                        :initial-marker="false"
                        :center="LocationManager.Center"
                        :markers="visibleLocations"
                        :zoom="zoom"
                        :fit-map-to-locations="LocationManager.FitMapToLocations.value"
                        :map-bounds="LocationManager.MapBounds"
                        @new-center="NewCenterCallback"
                        />
        </div>
    </div>
</template>

<script lang="ts">
  import { computed, ref, onMounted, triggerRef } from 'vue';
  import GetLocationTypeManager from "../helpers/LocationTypeManager";
  import { debounce } from "lodash"
  import GetCustomFilterManager from "../helpers/CustomFilterManager";
  import GetSearchManager from "../helpers/SearchManager";
  import LocationManagerClass from "../helpers/LocationManager";
  import { formatPhoneNumber } from '../helpers/formatting';
  import TagFilter from './TagFilter.vue';
  import GoogleMap from './GoogleMap.vue';
  import LocationListItem from "./LocationListItem.vue";
  import { ICenter, ILocationType } from '../helpers/LocationFinderTypes';
  import LocationPin from "./LocationPin.vue";
  import { Loader } from '@googlemaps/js-api-loader';

  export default {
    components: { TagFilter, GoogleMap, LocationPin, LocationListItem },
    name: 'LocationFinder',
    props: {
      apiKey: {
        type: String,
        required: true,
      },
      center: {
        type: Object || null,
        required: true,
      },
      config: {
        type: Object,
        required: true
      },
      cssClass: {
        type: String,
        required: false,
        default: '',
      },
      detailsUrl: {
        type: String,
        required: true,
      },
      locations: {
        type: Array || null,
        required: true,
      },
      locationTypes: {
        type: Array || null,
        required: true,
      },
      customFilters: {
        type: Array || null,
        required: true,
      },
      zoom: {
        type: Number,
        required: false,
        default: 10,
      },
      filterType: {
        type: String,
        required: true,
      },
      enableDetails: {
        type: Boolean,
        required: true,
      }
    },
    setup(props) {
      // Initialize Managers
      const LocationTypeManager = GetLocationTypeManager(props.locationTypes, props.config.SbEnable);
      const CustomFilterManager = GetCustomFilterManager(props.customFilters)
      const SearchManager = GetSearchManager();
      const LocationManager = new LocationManagerClass(
        CustomFilterManager,
        LocationTypeManager,
        SearchManager,
        props.locations,
        props.center,
        props.detailsUrl,
        props.apiKey,
        props.config.SbMaxRadius,
        props.config.SbEnable,
        props.enableDetails
      );

      let searchPlaceholder = "Search..."
      const visibleLocations = LocationManager.GetVisibleLocations()

      let timeout: ReturnType<typeof setTimeout>

      const debounceNewCenter = (center: ICenter): void => {
        LocationManager.Center = center;
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          LocationManager.GetDataFromApi(center);
        }, 250);
      }

      const sharedSourceClickHandler = (opt) => LocationTypeManager.ToggleExternalSource(opt.Id, props.config.SbEnabled);
      const filterClickHadler = (opt) => CustomFilterManager.ToggleFilter(opt.Id);
      const NewCenterCallback = (center: ICenter) => debounceNewCenter(center);

      const ourBranchClickHandler = (opt: ILocationType) => {
        LocationTypeManager.ToggleInternalSource(opt.Id, (manager) => {
          SearchManager.Search = ""
          if (!manager.IsAnyInternalSourceSelected()) {
            CustomFilterManager.SetAllFiltersSelectionTo(false)
          } else {
            CustomFilterManager.SetAllFiltersSelectionTo(true)
          }
        })
      }

      const setMapFromUsersLocation = (location) => {
        SearchManager.Search = "";
        const newCenter: ICenter = { lat: location.coords.latitude, lng: location.coords.longitude };
        LocationManager.Center = newCenter;
        LocationTypeManager.Counter++;
      };

      const getLocation = () => {
        if (window.navigator && window.navigator.geolocation) {
          window.navigator.geolocation.getCurrentPosition(
            setMapFromUsersLocation,
            (err) => console.error(err)
          );
        }
      };

      onMounted(() => {
        LocationManager.GetDataFromApi()
        //Force re computting visible locations
        setTimeout(() => {
          LocationTypeManager.Counter++;
        }, 500)
      });

      return {
        formatPhoneNumber,
        getLocation,
        filterClickHadler,
        visibleLocations,
        searchPlaceholder,
        CustomFilterManager,
        sharedSourceClickHandler,
        LocationTypeManager,
        ourBranchClickHandler,
        LocationManager,
        SearchManager,
        NewCenterCallback,
      };
    }
  }
</script>

<style lang="scss" scoped></style>
